// src/components/Dashboard/MarketingDashboard.tsx
// ============================================
// Marketing Dashboard - 第二页：营销分析（Sprint 8 Epic 4）
//
// 布局：
//   - 顶部：平台切换 Tab（公众号 / 小红书 / 抖音企业号 / 抖音个人号 / 知乎）
//   - 上：6 个核心指标卡片（播放/点赞/收藏/评论/转发/私信）
//   - 中：粉丝增长 + 趋势图（双折线）
//   - 下：爆款内容 Top 5
//
// 数据来源：useDashboardData.marketing
// ============================================

import { useMemo } from "react";
import { motion } from "framer-motion";
import {
  Area,
  AreaChart,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import { Eye, Heart, Bookmark, MessageCircle, Share2, Mail, TrendingUp } from "lucide-react";
import {
  DashboardCard,
  KpiCell,
  EmptyState,
  FadeIn,
  Tag,
  formatViews,
} from "./shared";
import type {
  MarketingData,
  MarketingPlatform,
  PlatformData,
} from "../../hooks/useDashboardData";

// 导出 MarketingPlatform 类型供外部组件使用
export type { MarketingPlatform };

interface MarketingDashboardProps {
  data: MarketingData;
  /** 当前激活的平台 Tab（受控） */
  active: MarketingPlatform;
  /** 平台 Tab 切换回调 */
  onActiveChange: (platform: MarketingPlatform) => void;
}

// ============================================
// 平台元信息
// ============================================

interface PlatformMeta {
  id: MarketingPlatform;
  label: string;
  shortLabel: string;
  color: string;
  /** 平台图标（emoji，与 PLATFORM_CONFIG 保持一致） */
  icon?: string;
  /** 播放量字段别名 */
  viewsLabel: string;
}

const PLATFORMS: PlatformMeta[] = [
  {
    id: "wechat_official",
    label: "公众号",
    shortLabel: "WeChat",
    color: "#10B981",
    viewsLabel: "阅读量",
  },
  {
    id: "xiaohongshu",
    label: "小红书",
    shortLabel: "XHS",
    color: "#EF4444",
    viewsLabel: "曝光量",
  },
  {
    id: "douyin_enterprise",
    label: "抖音企业号",
    shortLabel: "DY-Ent",
    color: "#000000",
    icon: "🎬",
    viewsLabel: "播放量",
  },
  {
    id: "douyin_personal",
    label: "抖音个人号",
    shortLabel: "DY-Per",
    color: "#000000",
    icon: "🎭",
    viewsLabel: "播放量",
  },
  {
    id: "zhihu",
    label: "知乎",
    shortLabel: "Zhihu",
    color: "#3B82F6",
    viewsLabel: "浏览量",
  },
  {
    id: "bilibili",
    label: "B站",
    shortLabel: "Bili",
    color: "#FB7299",
    viewsLabel: "播放量",
  },
];

// 平台配置状态检查（绿=已配置，红=未配置）
function isPlatformConfigured(platformId: MarketingPlatform): boolean {
  try {
    // 从 localStorage 读取 apiConfig
    const stored = localStorage.getItem("xeaos_api_configs");
    if (!stored) {
      // 首次加载未持久化时，检查环境变量（微信公众号已有 .env 配置）
      if (platformId === "wechat_official") {
        const env = (import.meta as any).env;
        return !!(env?.VITE_WECHAT_APP_ID);
      }
      return false;
    }
    const configs = JSON.parse(stored);
    switch (platformId) {
      case "wechat_official":
        // 微信公众号：检查 appId 是否非空（.env 已配置则也为 true）
        const env = (import.meta as any).env;
        return !!(
          configs.wechat?.extra?.appId ||
          env?.VITE_WECHAT_APP_ID
        );
      case "xiaohongshu":
        // 小红书：检查是否配置（目前无独立 API，视为待配置）
        return !!configs.xiaohongshu?.enabled;
      case "douyin_enterprise":
        // 抖音企业号：检查是否配置（目前无独立 API，视为待配置）
        return !!configs.douyin_enterprise?.enabled;
      case "douyin_personal":
        // 抖音个人号：检查是否配置（目前无独立 API，视为待配置）
        return !!configs.douyin_personal?.enabled;
      case "zhihu":
        // 知乎：检查是否配置（目前无独立 API，视为待配置）
        return !!configs.zhihu?.enabled;
      case "bilibili":
        // B站：检查是否配置（目前无独立 API，视为待配置）
        return !!configs.bilibili?.enabled;
      default:
        return false;
    }
  } catch {
    return false;
  }
}

export default function MarketingDashboard({ data, active, onActiveChange }: MarketingDashboardProps) {
  const activeMeta = PLATFORMS.find((p) => p.id === active)!;
  const platformData: PlatformData = data.platforms[active] || {
    platform: active,
    metrics: {
      views: 0,
      likes: 0,
      favorites: 0,
      comments: 0,
      shares: 0,
      dm: 0,
      followersGain: 0,
      followersTotal: 0,
    },
    trend: [],
    topContent: [],
    degraded: true,
  };
  const { metrics, trend, topContent, degraded, dataSource } = platformData;

  // 趋势图最大值
  const trendMax = useMemo(
    () => trend.reduce((max, p) => Math.max(max, p.views), 0) || 1,
    [trend]
  );

  return (
    <div className="space-y-3">
      {/* v6.3.18 数据来源标注条：直播面板要真实数据——真实来源 / 未接入原因，不空不造假 */}
      {dataSource && (
        <FadeIn>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-[#1e2a3f]/60 bg-[#0a0e1a]/60 backdrop-blur-md">
            <span className="w-1.5 h-1.5 rounded-full bg-cyan-400/80 flex-shrink-0" />
            <span className="text-[10px] font-mono text-gray-400">
              数据来源：{dataSource}
            </span>
          </div>
        </FadeIn>
      )}
      {/* ============================================ */}
      {/* 顶部：平台切换 Tab（含配置状态绿/红指示） */}
      {/* ============================================ */}
      <FadeIn>
        <div className="flex items-center gap-1.5 p-1 rounded-lg border border-[#1e2a3f]/60 bg-[#0a0e1a]/60 backdrop-blur-md">
          {PLATFORMS.map((p) => {
            const isActive = p.id === active;
            // 平台配置状态：绿色=已配置，红色=未配置
            const platformConfigured = isPlatformConfigured(p.id);
            return (
              <button
                key={p.id}
                onClick={() => onActiveChange(p.id)}
                className={`relative flex-1 flex flex-col items-center py-1.5 px-2 rounded-md transition-all ${
                  isActive ? "bg-[#131826]" : "hover:bg-white/[0.03]"
                }`}
              >
                {isActive && (
                  <motion.div
                    layoutId="marketing-tab"
                    className="absolute top-0 left-2 right-2 h-px"
                    style={{ background: p.color }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                  />
                )}
                <div className="flex items-center gap-1">
                  {p.icon && (
                    <span className="text-[11px] leading-none">{p.icon}</span>
                  )}
                  <span
                    className={`text-[12px] font-semibold ${
                      isActive ? "text-white" : "text-gray-500"
                    }`}
                  >
                    {p.label}
                  </span>
                  {/* 配置状态指示点：绿=已配置，红=未配置 */}
                  <span
                    className={`w-1.5 h-1.5 rounded-full ${
                      platformConfigured ? "bg-emerald-400" : "bg-red-400"
                    }`}
                    title={platformConfigured ? "已配置" : "未配置（请在设置页配置）"}
                  />
                </div>
                <span className="text-[9px] font-mono text-gray-600 uppercase tracking-wider">
                  {p.shortLabel}
                </span>
              </button>
            );
          })}
        </div>
      </FadeIn>

      {/* ============================================ */}
      {/* 6 个核心指标卡片 */}
      {/* ============================================ */}
      <FadeIn delay={0.05}>
        <div className="grid grid-cols-6 gap-2.5">
          <KpiCell
            label={activeMeta.viewsLabel}
            value={metrics.views}
            format={(v) => formatViews(v)}
            color={activeMeta.color}
            subtitle="Views"
            degraded={degraded}
          />
          <KpiCell
            label="点赞"
            value={metrics.likes}
            format={(v) => formatViews(v)}
            color="#EF4444"
            subtitle="Likes"
            degraded={degraded}
          />
          <KpiCell
            label="收藏"
            value={metrics.favorites}
            format={(v) => formatViews(v)}
            color="#F59E0B"
            subtitle="Favorites"
            degraded={degraded}
          />
          <KpiCell
            label="评论"
            value={metrics.comments}
            format={(v) => formatViews(v)}
            color="#3B82F6"
            subtitle="Comments"
            degraded={degraded}
          />
          <KpiCell
            label="转发"
            value={metrics.shares}
            format={(v) => formatViews(v)}
            color="#A855F7"
            subtitle="Shares"
            degraded={degraded}
          />
          <KpiCell
            label="私信"
            value={metrics.dm}
            format={(v) => formatViews(v)}
            color="#EC4899"
            subtitle="DM"
            degraded={degraded}
          />
        </div>
      </FadeIn>

      {/* ============================================ */}
      {/* 趋势图 + 粉丝增长 */}
      {/* ============================================ */}
      <div className="grid grid-cols-12 gap-3">
        {/* 趋势图（左 8 列） */}
        <FadeIn delay={0.1} className="col-span-8">
          <DashboardCard
            title={`${activeMeta.label} 数据趋势`}
            subtitle="14D Trend"
            accentColor={activeMeta.color}
            extra={
              <div className="flex items-center gap-3 text-[10px] font-mono">
                <span className="flex items-center gap-1">
                  <span
                    className="w-2 h-2 rounded-sm"
                    style={{ background: activeMeta.color }}
                  />
                  <span className="text-gray-400">{activeMeta.viewsLabel}</span>
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-sm bg-red-500/80" />
                  <span className="text-gray-400">点赞</span>
                </span>
              </div>
            }
            bodyClassName="h-[200px]"
          >
            {degraded || trend.length === 0 ? (
              <EmptyState degraded={degraded || trend.length === 0} reason={dataSource} />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={trend}
                  margin={{ top: 8, right: 8, left: -8, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="mktViews" x1="0" y1="0" x2="0" y2="1">
                      <stop
                        offset="0%"
                        stopColor={activeMeta.color}
                        stopOpacity={0.5}
                      />
                      <stop
                        offset="100%"
                        stopColor={activeMeta.color}
                        stopOpacity={0}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    stroke="#1e2a3f"
                    strokeDasharray="3 3"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="date"
                    tick={{
                      fill: "#6B7280",
                      fontSize: 10,
                      fontFamily: "monospace",
                    }}
                    tickLine={false}
                    axisLine={{ stroke: "#1e2a3f" }}
                    tickFormatter={(v: string) => v.slice(5)}
                  />
                  <YAxis
                    tick={{
                      fill: "#6B7280",
                      fontSize: 10,
                      fontFamily: "monospace",
                    }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v: number) => formatViews(v)}
                    domain={[0, Math.ceil(trendMax * 1.2)]}
                  />
                  <Tooltip
                    contentStyle={{
                      background: "#0a0e1a",
                      border: "1px solid #1e2a3f",
                      borderRadius: 8,
                      fontSize: 11,
                      fontFamily: "monospace",
                    }}
                    labelStyle={{ color: "#9CA3AF" }}
                  />
                  <Area
                    type="monotone"
                    dataKey="views"
                    stroke={activeMeta.color}
                    strokeWidth={1.5}
                    fill="url(#mktViews)"
                  />
                  <Line
                    type="monotone"
                    dataKey="likes"
                    stroke="#EF4444"
                    strokeWidth={1.2}
                    dot={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </DashboardCard>
        </FadeIn>

        {/* 粉丝增长（右 4 列） */}
        <FadeIn delay={0.15} className="col-span-4">
          <DashboardCard
            title="粉丝增长"
            subtitle="Followers"
            accentColor="#A855F7"
            extra={<TrendingUp className="w-3 h-3 text-purple-400" />}
            bodyClassName="h-[200px] flex flex-col"
          >
            <div className="grid grid-cols-2 gap-2 mb-3">
              <div className="rounded-md border border-[#1e2a3f]/60 bg-[#0a0e1a]/60 px-2 py-1.5">
                <div className="text-[9px] font-mono text-gray-500 uppercase">
                  增长
                </div>
                <div className="text-[16px] font-mono font-bold text-emerald-400">
                  {degraded ? "--" : `+${metrics.followersGain}`}
                </div>
              </div>
              <div className="rounded-md border border-[#1e2a3f]/60 bg-[#0a0e1a]/60 px-2 py-1.5">
                <div className="text-[9px] font-mono text-gray-500 uppercase">
                  总数
                </div>
                <div className="text-[16px] font-mono font-bold text-white">
                  {degraded ? "--" : formatViews(metrics.followersTotal)}
                </div>
              </div>
            </div>
            <div className="flex-1">
              {degraded || trend.length === 0 ? (
                <EmptyState degraded={degraded || trend.length === 0} reason={dataSource} />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={trend}
                    margin={{ top: 4, right: 4, left: -16, bottom: 0 }}
                  >
                    <XAxis dataKey="date" hide />
                    <YAxis hide />
                    <Tooltip
                      contentStyle={{
                        background: "#0a0e1a",
                        border: "1px solid #1e2a3f",
                        borderRadius: 6,
                        fontSize: 10,
                        fontFamily: "monospace",
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="likes"
                      stroke="#A855F7"
                      strokeWidth={1.5}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </DashboardCard>
        </FadeIn>
      </div>

      {/* ============================================ */}
      {/* 爆款内容 Top 5 */}
      {/* ============================================ */}
      <FadeIn delay={0.2}>
        <DashboardCard
          title="爆款内容 Top 5"
          subtitle={`${activeMeta.label} · Top Content`}
          accentColor="#F59E0B"
          extra={
            <Tag color="amber">
              {degraded ? "未接入" : `${topContent.length} ITEMS`}
            </Tag>
          }
          bodyClassName="min-h-[180px]"
        >
          {degraded ? (
            <EmptyState degraded reason={dataSource} />
          ) : topContent.length === 0 ? (
            <EmptyState message="暂无爆款内容" />
          ) : (
            <div className="space-y-0.5">
              {topContent.map((c, i) => (
                <div
                  key={c.id}
                  className="flex items-center gap-3 py-1.5 px-2 border-b border-[#1e2a3f]/40 last:border-b-0 hover:bg-white/[0.02] rounded"
                >
                  <span
                    className={`text-[14px] font-mono font-bold w-6 text-center ${
                      i === 0
                        ? "text-amber-400"
                        : i === 1
                        ? "text-gray-300"
                        : i === 2
                        ? "text-orange-700"
                        : "text-gray-600"
                    }`}
                  >
                    {i + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] font-mono text-gray-200 truncate">
                      {c.title}
                    </div>
                    <div className="text-[9px] font-mono text-gray-600">
                      {new Date(c.publishedAt).toLocaleDateString("zh-CN")}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-[10px] font-mono flex-shrink-0">
                    <span className="flex items-center gap-1 text-gray-300">
                      <Eye className="w-2.5 h-2.5" />
                      {formatViews(c.views)}
                    </span>
                    <span className="flex items-center gap-1 text-red-400">
                      <Heart className="w-2.5 h-2.5" />
                      {formatViews(c.likes)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DashboardCard>
      </FadeIn>

      {/* ============================================ */}
      {/* 平台对比条 */}
      {/* ============================================ */}
      <FadeIn delay={0.25}>
        <div className="flex items-center justify-between px-3 py-2 rounded-lg border border-[#1e2a3f]/50 bg-[#0a0e1a]/40">
          <div className="flex items-center gap-3 text-[10px] font-mono">
            <span className="flex items-center gap-1">
              <Eye className="w-3 h-3 text-cyan-400" />
              <span className="text-gray-400">Views</span>
            </span>
            <span className="flex items-center gap-1">
              <Heart className="w-3 h-3 text-red-400" />
              <span className="text-gray-400">Likes</span>
            </span>
            <span className="flex items-center gap-1">
              <Bookmark className="w-3 h-3 text-amber-400" />
              <span className="text-gray-400">Favorites</span>
            </span>
            <span className="flex items-center gap-1">
              <MessageCircle className="w-3 h-3 text-blue-400" />
              <span className="text-gray-400">Comments</span>
            </span>
            <span className="flex items-center gap-1">
              <Share2 className="w-3 h-3 text-purple-400" />
              <span className="text-gray-400">Shares</span>
            </span>
            <span className="flex items-center gap-1">
              <Mail className="w-3 h-3 text-pink-400" />
              <span className="text-gray-400">DM</span>
            </span>
          </div>
          {degraded && <Tag color="amber">DEGRADED MODE</Tag>}
        </div>
      </FadeIn>
    </div>
  );
}
